import React from 'react';
import { CancelReason } from '../../../../types';
import {
  PrimaryButton,
  PrimaryInput,
  PrimarySelect,
  SecondaryButton,
} from '../../../components';
import { toast } from 'react-toastify';
import { ordersApi } from '../../../../api';

interface EditCancelReasonProps {
  reason: CancelReason | null;
  close: () => void;
  refresh: () => void;
}

interface CancelReasonFormData {
  reason: string;
  refundType: 'NO_REFUND' | 'FULL_REFUND' | 'MEAL_ONLY';
  autoRefund: boolean;
  includeDelivery: boolean;
  includeService: boolean;
}

const refundTypeOptions = [
  { id: 'NO_REFUND', name: 'No Refund' },
  { id: 'FULL_REFUND', name: 'Full Refund' },
  { id: 'MEAL_ONLY', name: 'Product Refund Only' },
];

const booleanOptions = [
  { id: 'true', name: 'Yes' },
  { id: 'false', name: 'No' },
];

export const EditCancelReason: React.FC<EditCancelReasonProps> = ({
  reason,
  close,
  refresh,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [formData, setFormData] = React.useState<CancelReasonFormData>({
    reason: reason?.reason || '',
    refundType: reason?.refundType || 'NO_REFUND',
    autoRefund: reason?.autoRefund || false,
    includeDelivery: reason?.includeDelivery || false,
    includeService: reason?.includeService || false,
  });

  const isAutoRefundDisabled = formData.refundType === 'NO_REFUND';
  const areFeeFieldsDisabled =
    formData.refundType === 'NO_REFUND' ||
    formData.refundType === 'FULL_REFUND' ||
    formData.refundType === 'MEAL_ONLY';

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'refundType') {
      if (value === 'NO_REFUND') {
        // When NO_REFUND is selected, auto-set all refund options to false
        setFormData({
          reason: formData.reason,
          refundType: 'NO_REFUND',
          autoRefund: false,
          includeDelivery: false,
          includeService: false,
        });
      } else if (value === 'FULL_REFUND') {
        // When FULL_REFUND is selected, set delivery and service fees to true
        setFormData((prev) => ({
          ...prev,
          refundType: 'FULL_REFUND',
          includeDelivery: true,
          includeService: true,
        }));
      } else if (value === 'MEAL_ONLY') {
        // When MEAL_ONLY is selected, set delivery and service fees to false
        setFormData((prev) => ({
          ...prev,
          refundType: 'MEAL_ONLY',
          includeDelivery: false,
          includeService: false,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          refundType: value as 'NO_REFUND' | 'FULL_REFUND' | 'MEAL_ONLY',
        }));
      }
    } else if (
      name === 'autoRefund' ||
      name === 'includeDelivery' ||
      name === 'includeService'
    ) {
      setFormData((prev) => ({
        ...prev,
        [name]: value === 'true',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.reason.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        ...(reason && { id: reason.id }),
      };

      if (reason && reason.id) {
        await ordersApi.updateCancelReason(reason.id, payload);
        toast.success('Cancel reason updated successfully');
      } else {
        await ordersApi.createCancelReason(payload);
        toast.success('Cancel reason created successfully');
      }

      refresh();
      close();
    } catch (error) {
      console.error('Error saving cancel reason:', error);
      toast.error('Failed to save cancel reason');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 h-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="bold text-2xl capitalize">
          {reason ? 'Edit Cancel Reason' : 'Create Cancel Reason'}
        </h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <PrimaryInput
            label="Reason"
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            placeholder="Enter cancel reason"
            required
            className="w-full"
          />

          <PrimarySelect
            label="Refund Type"
            name="refundType"
            value={formData.refundType}
            onChange={handleInputChange}
            options={refundTypeOptions}
            optionLabel="name"
            optionValue="id"
            placeholder="Select refund type"
            className="w-full"
          />

          <PrimarySelect
            label="Auto Refund"
            name="autoRefund"
            value={formData.autoRefund.toString()}
            onChange={handleInputChange}
            options={booleanOptions}
            optionLabel="name"
            optionValue="id"
            placeholder="Select auto refund option"
            className="w-full"
            disabled={isAutoRefundDisabled}
          />

          <PrimarySelect
            label="Include Delivery Fee in Refund"
            name="includeDelivery"
            value={formData.includeDelivery.toString()}
            onChange={handleInputChange}
            options={booleanOptions}
            optionLabel="name"
            optionValue="id"
            placeholder="Include delivery fee"
            className="w-full"
            disabled={areFeeFieldsDisabled}
          />

          <PrimarySelect
            label="Include Service Fee in Refund"
            name="includeService"
            value={formData.includeService.toString()}
            onChange={handleInputChange}
            options={booleanOptions}
            optionLabel="name"
            optionValue="id"
            placeholder="Include service fee"
            className="w-full"
            disabled={areFeeFieldsDisabled}
          />
        </div>

        <div className="flex justify-end space-x-4 mt-8">
          <SecondaryButton text="Cancel" onClick={close} type="button" />
          <PrimaryButton
            text={
              loading ? 'Saving...' : reason ? 'Update Reason' : 'Create Reason'
            }
            type="submit"
            disabled={loading || !formData.reason.trim()}
          />
        </div>
      </form>
    </div>
  );
};
